/**
 * BizFirst Utility Functions
 *
 * Common utility functions used across BizFirst applications
 */

/**
 * Safely parse JSON with fallback
 */
export function parseJSON<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json) as T
  } catch {
    return fallback
  }
}

/**
 * Safely stringify JSON with fallback
 */
export function stringifyJSON(value: any, fallback = '{}'): string {
  try {
    return JSON.stringify(value)
  } catch {
    return fallback
  }
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Throttle function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

/**
 * Merge objects deeply
 */
export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target } as any

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key]
      if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
        result[key] = deepMerge(result[key] || {}, sourceValue as any)
      } else {
        result[key] = sourceValue
      }
    }
  }

  return result
}

/**
 * Clone object deeply
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime()) as any
  if (obj instanceof Array) return obj.map((item) => deepClone(item)) as any
  if (obj instanceof Object) {
    const cloneObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloneObj[key] = deepClone(obj[key])
      }
    }
    return cloneObj
  }
  return obj
}

/**
 * Format bytes to human readable size
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/**
 * Format timestamp to readable date
 */
export function formatDate(timestamp: string | Date | number, locale = 'en-US'): string {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(timestamp)
    return date.toLocaleDateString(locale)
  } catch {
    return 'Invalid date'
  }
}

/**
 * Format time to HH:MM:SS
 */
export function formatTime(timestamp: string | Date | number): string {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(timestamp)
    return date.toLocaleTimeString('en-US', { hour12: false })
  } catch {
    return 'Invalid time'
  }
}

/**
 * Check if value is empty
 */
export function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim().length === 0
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

/**
 * Resolves an API base URL against the current page's origin.
 * - Empty/unset value -> `${origin}` (same-origin default; endpoint paths
 *   already include their own leading "/api/..." segment — see
 *   e.g. api-client-js/src/form.client.ts — so this must NOT also append
 *   "/api", or requests double up into "/api/api/...")
 * - Relative path (e.g. "/custom-api") -> resolved against `${origin}`
 * - Protocol-relative URL (e.g. "//host/path") -> returned unchanged
 * - Absolute URL (http:// or https://) -> returned unchanged
 *
 * Lets a single VITE_API_BASE_URL env var express a same-origin default,
 * a custom same-origin path, or a fully separate API host — without ever
 * hardcoding a URL in application code.
 */
export function calculateApiUrl(value?: string): string {
  const trimmed = (value ?? '').trim()
  if (/^(https?:)?\/\//i.test(trimmed)) return trimmed
  if (!trimmed) return typeof window === 'undefined' ? '' : window.location.origin
  const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  if (typeof window === 'undefined') return normalizedPath
  return `${window.location.origin}${normalizedPath}`
}

/**
 * Generate a cryptographically random GUID (UUID v4).
 * Uses crypto.getRandomValues() — broader support than crypto.randomUUID()
 * and works in non-secure (HTTP) contexts.
 */
export function createGuid(): string {
  const b = new Uint8Array(16)
  crypto.getRandomValues(b)
  b[6] = (b[6]! & 0x0f) | 0x40 // version 4
  b[8] = (b[8]! & 0x3f) | 0x80 // variant RFC 4122
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

/**
 * Generate unique ID
 */
export function generateId(prefix = ''): string {
  return `${prefix}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Delay promise
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Retry function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options = { maxRetries: 3, initialDelay: 100 }
): Promise<T> {
  let lastError: Error

  for (let i = 0; i <= options.maxRetries; i++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error as Error
      if (i < options.maxRetries) {
        const delayMs = options.initialDelay * Math.pow(2, i)
        await delay(delayMs)
      }
    }
  }

  throw lastError!
}

/**
 * Memoize function result
 */
export function memoize<T extends (...args: any[]) => any>(fn: T): T {
  const cache = new Map()

  return ((...args: any[]) => {
    const key = JSON.stringify(args)
    if (cache.has(key)) {
      return cache.get(key)
    }
    const result = fn(...args)
    cache.set(key, result)
    return result
  }) as T
}

/**
 * Utilities object
 */
export const Utils = {
  createGuid,
  calculateApiUrl,
  parseJSON,
  stringifyJSON,
  debounce,
  throttle,
  deepMerge,
  deepClone,
  formatBytes,
  formatDate,
  formatTime,
  isEmpty,
  generateId,
  delay,
  retryWithBackoff,
  memoize,
}

export default Utils
